/**
 * IPAM API Service
 *
 * Service for IP Address Management (IPAM) - managing IP subnets,
 * allocations, and reservations within Network Pools.
 */

import type {
  IPSubnet,
  IPSubnetCreate,
  IPSubnetUpdate,
  IPAllocation,
  IPAllocationCreate,
  IPReservation,
  IPReservationCreate,
  IPAvailability,
  IPConflict,
  SubnetConflictsResponse,
} from '../types/network'
import { apiClient } from './api'

export const ipamApi = {
  // ========================================
  // Subnets
  // ========================================

  /**
   * Create a new IP subnet within a network pool
   */
  async createSubnet(data: IPSubnetCreate): Promise<IPSubnet> {
    return await apiClient.post<IPSubnet>('/api/v1/ipam/subnets', data)
  },

  /**
   * Get details of a specific subnet
   */
  async getSubnet(subnetId: string): Promise<IPSubnet> {
    return await apiClient.get<IPSubnet>(`/api/v1/ipam/subnets/${subnetId}`)
  },

  /**
   * List subnets for a specific network pool
   */
  async listSubnets(poolId: string): Promise<{ total: number; subnets: IPSubnet[] }> {
    return await apiClient.get<{ total: number; subnets: IPSubnet[] }>(
      `/api/v1/ipam/network-pools/${poolId}/subnets`,
    )
  },

  /**
   * Update a subnet
   */
  async updateSubnet(subnetId: string, data: IPSubnetUpdate): Promise<IPSubnet> {
    return await apiClient.put<IPSubnet>(`/api/v1/ipam/subnets/${subnetId}`, data)
  },

  /**
   * Delete a subnet
   */
  async deleteSubnet(subnetId: string): Promise<void> {
    await apiClient.delete(`/api/v1/ipam/subnets/${subnetId}`)
  },

  // ========================================
  // IP Allocations
  // ========================================

  /**
   * Allocate an IP address (specific or next available)
   */
  async allocateIP(data: IPAllocationCreate): Promise<IPAllocation> {
    return await apiClient.post<IPAllocation>('/api/v1/ipam/allocations', data)
  },

  /**
   * Get details of an IP allocation
   */
  async getAllocation(allocationId: string): Promise<IPAllocation> {
    return await apiClient.get<IPAllocation>(`/api/v1/ipam/allocations/${allocationId}`)
  },

  /**
   * List IP allocations for a subnet
   */
  async listAllocations(subnetId: string): Promise<IPAllocation[]> {
    return await apiClient.get<IPAllocation[]>(`/api/v1/ipam/subnets/${subnetId}/allocations`)
  },

  /**
   * Release (deallocate) an IP address
   */
  async releaseIP(allocationId: string): Promise<void> {
    await apiClient.delete(`/api/v1/ipam/allocations/${allocationId}`)
  },

  // ========================================
  // IP Reservations
  // ========================================

  /**
   * Reserve an IP address for infrastructure use
   */
  async reserveIP(data: IPReservationCreate): Promise<IPReservation> {
    return await apiClient.post<IPReservation>('/api/v1/ipam/reservations', data)
  },

  /**
   * Get details of an IP reservation
   */
  async getReservation(reservationId: string): Promise<IPReservation> {
    return await apiClient.get<IPReservation>(`/api/v1/ipam/reservations/${reservationId}`)
  },

  /**
   * List IP reservations for a subnet
   */
  async listReservations(subnetId: string): Promise<IPReservation[]> {
    return await apiClient.get<IPReservation[]>(`/api/v1/ipam/subnets/${subnetId}/reservations`)
  },

  /**
   * Remove an IP reservation
   */
  async removeReservation(reservationId: string): Promise<void> {
    await apiClient.delete(`/api/v1/ipam/reservations/${reservationId}`)
  },

  // ========================================
  // Utilities
  // ========================================

  /**
   * Get IP availability information for a subnet
   */
  async getAvailability(subnetId: string): Promise<IPAvailability> {
    return await apiClient.get<IPAvailability>(`/api/v1/ipam/subnets/${subnetId}/availability`)
  },

  /**
   * Detect IP conflicts in a subnet
   */
  async detectConflicts(subnetId: string): Promise<IPConflict[]> {
    return await apiClient.get<IPConflict[]>(`/api/v1/ipam/subnets/${subnetId}/conflicts`)
  },

  // ========================================
  // Subnet-to-Subnet Conflicts
  // ========================================

  /**
   * Detect conflicts between subnets in a network pool
   */
  async detectSubnetConflictsByPool(poolId: string): Promise<SubnetConflictsResponse> {
    return await apiClient.get<SubnetConflictsResponse>(
      `/api/v1/ipam/network-pools/${poolId}/subnet-conflicts`,
    )
  },

  /**
   * Detect conflicts between all subnets in a cluster
   */
  async detectSubnetConflictsByCluster(clusterId: string): Promise<SubnetConflictsResponse> {
    return await apiClient.get<SubnetConflictsResponse>(
      `/api/v1/ipam/clusters/${clusterId}/subnet-conflicts`,
    )
  },
}

// ========================================
// Helper Functions
// ========================================

/**
 * Calculate utilization percentage for a subnet
 */
export function calculateUtilization(subnet: IPSubnet): number {
  if (subnet.total_ips === 0) return 0
  const used = subnet.allocated_ips + subnet.reserved_ips
  return Math.round((used / subnet.total_ips) * 100)
}

/**
 * Get color class based on utilization percentage
 */
export function getUtilizationColor(percentage: number): string {
  if (percentage >= 90) return 'text-red-600 bg-red-100'
  if (percentage >= 75) return 'text-orange-600 bg-orange-100'
  if (percentage >= 50) return 'text-yellow-600 bg-yellow-100'
  return 'text-green-600 bg-green-100'
}

/**
 * Get progress bar color based on utilization
 */
export function getProgressBarColor(percentage: number): string {
  if (percentage >= 90) return 'bg-red-500'
  if (percentage >= 75) return 'bg-orange-500'
  if (percentage >= 50) return 'bg-yellow-500'
  return 'bg-green-500'
}

/**
 * Format IP count display
 */
export function formatIPCount(subnet: IPSubnet): string {
  const available = subnet.total_ips - subnet.allocated_ips - subnet.reserved_ips
  return `${available} disponíveis • ${subnet.allocated_ips} alocados • ${subnet.reserved_ips} reservados`
}

/**
 * Check if subnet is near capacity
 */
export function isSubnetNearCapacity(subnet: IPSubnet, threshold: number = 80): boolean {
  const utilization = calculateUtilization(subnet)
  return utilization >= threshold
}

/**
 * Get next available IP from availability response
 */
export function getNextAvailableIP(availability: IPAvailability): string | null {
  return availability.next_available_ip || null
}
